import { Component, Element, Prop, h } from '@stencil/core';

import { AutoCheckState } from '../../../store/store.autocheck';
import { ConsumerMarketConfidenceState, initialState as consumerMarketConfidenceInitialState } from '../../../store/store.consumer-market-confidence';
import { OfferState, initialState } from '../../../store/store.offer';
import { OfferDetailsModal } from '../offer-details-modal/offer-details-modal';

@Component({
  tag: 'offer-certificate-review',
  styleUrl: 'offer-certificate-review.css',
})
export class OfferCertificateReview {
  @Element() el: HTMLElement;
  dealerListElem!: HTMLElement;

  @Prop() autoCheck: AutoCheckState;

  @Prop() customer = { email: '', firstName: '', lastName: '', phone: '', zip: '' };

  @Prop() dealer = {
    allocated: [],
    dealerOffers: {},
    buyingCenters: [],
    isOfficeClosed: false,
    name: '',
    numAssignableDealers: 0,
    dealerAppointmentInfo: null,
  };

  @Prop() offer: OfferState = initialState;

  @Prop() vehicle = {
    accidents: false,
    auctionedRecently: false,
    color: '',
    description: '',
    drivetrain: '',
    engine: '',
    fullName: '',
    hasReplacement: false,
    hasTwoSetsOfKeys: true,
    offerOptions: [],
    imageUrl: '',
    isAuctionedLast45: false,
    isRental: false,
    mileage: 0,
    originalOwner: false,
    rentalVehicle: false,
    replacementName: '',
    replacementText: '',
    replacementTypes: '',
    replacementVehicleYearMax: 2023,
    replacementVehicleYearMin: 2018,
    transmission: '',
    vin: '',
    yearMakeModel: '',
    yearId: 0,
    makeId: 0,
    modelId: 0,
    trimId: 0,
  };

  @Prop() optimizely = {
    icocertenhancementsOptimizely: false,
    isConsumerMarketConfidenceOn: false,
  };

  @Prop() showVehicleDetailDialog: (e: MouseEvent) => void = () => {};

  @Prop() consumerMarketConfidence: ConsumerMarketConfidenceState = consumerMarketConfidenceInitialState;

  scrollToDealerList = () => {
    this.dealerListElem.scrollIntoView();
  };

  render() {
    const { consumerMarketConfidence } = this;
    const { shouldDisplayExpectDealership, shouldDisplayConfidentVehicleHistoryReport, shouldDisplayDealerListInfo } = consumerMarketConfidence;
    const { autoCheckFullReportLink, reportDate, historyIcons } = this.autoCheck;
    const { email, zip } = this.customer;
    const { allocated, dealerOffers, buyingCenters, dealerAppointmentInfo, name } = this.dealer;
    const {
      expirationDate,
      isInspectionNeeded,
      isOfficeClosed,
      isFurtherReviewNeeded,
      offerId,
      isAssignedOffer,
      showCustomerDealerList,
      useTradeIn,
      tradeInBtnText,
      showIcoQuestionsSection,
      showDealerChosenReasons,
      isKelleyBuysYourCar,
    } = this.offer;
    const { fullName, imageUrl, vin } = this.vehicle;

    const canRequestOfferPrice = this.dealer.numAssignableDealers && this.offer.displayRequestAppointmentButton;
    const showKelleyBuysYourCar = isKelleyBuysYourCar;
    const shouldDisplayPrepareForVisit = isInspectionNeeded && showCustomerDealerList && consumerMarketConfidence.shouldDisplayPrepareForVisit;
    const historyReportClass = {
      'grid gap-7': true,
      'md:grid-cols-2 mb-8': shouldDisplayConfidentVehicleHistoryReport,
    };

    return (
      <certificate-layout showKelleyBuysYourCar={showKelleyBuysYourCar}>
        <offer-message-needs-review
          email={email}
          isOfficeClosed={isOfficeClosed}
          isFurtherReviewNeeded={isFurtherReviewNeeded}
          isInspectionNeeded={isInspectionNeeded}
          offerId={offerId}
          offerExpiryDate={expirationDate}
          whenReviewClicked={this.showVehicleDetailDialog.bind(this)}
          whenAppointmentRequested={this.scrollToDealerList}
          vehicleShortDescriptions={fullName}
          vehicleImageLocation={imageUrl}
          zipCode={zip}
          slot="hero"
          dealerName={name}
          isAssigned={isAssignedOffer}
          showOfferPriceButton={canRequestOfferPrice}
          useTradeIn={useTradeIn}
          vehicle={this.vehicle}
          tradeInBtnText={tradeInBtnText}
          showIcoQuestionsSection={showIcoQuestionsSection}
          showKelleyBuysYourCar={showKelleyBuysYourCar}
        />
        <dealer-list
          shouldDisplayDealerListInfo={shouldDisplayDealerListInfo}
          expirationDate={expirationDate}
          allocatedDealers={allocated}
          dealerOffers={dealerOffers}
          buyingCenters={buyingCenters}
          dealerName={name}
          offerId={offerId}
          slot="main"
          dealerAppointmentInfo={dealerAppointmentInfo}
          showDealerChosenReasons={showDealerChosenReasons}
          ref={(el) => (this.dealerListElem = el as HTMLElement)}></dealer-list>

        <slot />
        <div class={historyReportClass}>
          <expect-message {...{ shouldDisplayExpectDealership }} />
          <confident-vehicle-history-report
            {...{
              autoCheckFullReportLink,
              reportGeneratedOnDate: reportDate,
              vin,
              shouldDisplayConfidentVehicleHistoryReport,
              historyIcons,
            }}
          />
        </div>
        <prepare-for-visit shouldDisplayPrepareForVisit={shouldDisplayPrepareForVisit} />
        <OfferDetailsModal shouldShowDealerComments={false} autoCheck={this.autoCheck} customer={this.customer} offer={this.offer} vehicle={this.vehicle} />

        <offer-certificate-disclaimer />
      </certificate-layout>
    );
  }
}
